import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { HERO } from "@/constants/testIds/landing";

const ROTATING = ["zur PDL.", "zur Praxisanleitung.", "zur Fachassistenz.", "zum Wundexperten.", "zur Betreuungskraft."];

export default function Hero() {
  const [wordIdx, setWordIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setWordIdx((i) => (i + 1) % ROTATING.length), 2600);
    return () => clearInterval(t);
  }, []);

  return (
    <section id="top" className="relative lg:min-h-[92vh] grid lg:grid-cols-2 overflow-hidden" style={{ backgroundColor: "#F6E8DC" }}>
      {/* LEFT · warm, friendly content */}
      <div className="flex items-center order-2 lg:order-1">
        <div className="w-full max-w-2xl mx-auto lg:mx-0 lg:ml-auto px-4 sm:px-6 lg:px-12 xl:px-16 py-16 lg:py-28">
          <div className="flex items-center gap-3.5 hero-rise" style={{ animationDelay: "120ms" }}>
            <span className="text-[11px] uppercase tracking-[0.34em] font-medium text-terra">
              Schön, dass du da bist
            </span>
            <span className="h-px w-14 bg-terra/50" />
          </div>

          <h1 className="font-magazine text-sand-800 leading-[1.08] text-[2.6rem] sm:text-[3.4rem] lg:text-[3.4rem] xl:text-[3.9rem] font-medium tracking-tight mt-6">
            <span className="block hero-rise" style={{ animationDelay: "220ms" }}>Du machst den Job.</span>
            <span className="block hero-rise" style={{ animationDelay: "340ms" }}>Wir machen dich</span>
            <span
              key={wordIdx}
              data-testid="hero-rotating-word"
              className="block text-terra hero-word"
            >
              {ROTATING[wordIdx]}
            </span>
          </h1>

          <p className="mt-8 text-sand-600 text-[16px] sm:text-[16.5px] leading-[1.8] max-w-md hero-rise" style={{ animationDelay: "500ms" }}>
            LG1/LG2, Praxisanleitung, PDL und Pflichtfortbildungen —
            AZAV-förderfähig, 100&nbsp;% online live. Und jeden Montag geht&apos;s los.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-4 hero-rise" style={{ animationDelay: "620ms" }}>
            <Button
              asChild
              data-testid={HERO.primaryCta}
              className="rounded-full bg-terra hover:bg-terra-dark text-cream-50 h-14 px-9 text-[14.5px] font-medium uppercase tracking-[0.16em] transition-colors"
            >
              <a href="#kontakt">
                Kostenlose Beratung
                <ArrowRight className="ml-2 h-4 w-4 nudge" strokeWidth={1.8} />
              </a>
            </Button>
            <a
              href="#kurse"
              data-testid={HERO.secondaryCta}
              className="text-[15.5px] text-sand-700 border-b border-sand-400/50 pb-0.5 hover:text-terra hover:border-terra transition-colors"
            >
              oder erst in Ruhe die Kurse ansehen
            </a>
          </div>

          <div data-testid="hero-bullets" className="mt-12 flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] uppercase tracking-[0.22em] font-semibold text-sand-500 hero-rise" style={{ animationDelay: "740ms" }}>
            <span>AZAV-zertifiziert</span>
            <span className="text-terra">·</span>
            <span>Start jeden Montag</span>
            <span className="text-terra">·</span>
            <span>Bildungsgutschein</span>
            <span className="text-terra">·</span>
            <span>Ratenzahlung ohne Aufschlag</span>
          </div>

          <a
            href="tel:+4921129273330"
            data-testid={HERO.phoneCta}
            className="mt-6 inline-block text-[13px] text-sand-500 hover:text-sand-800 hero-rise"
            style={{ animationDelay: "860ms" }}
          >
            oder direkt: <span className="text-sand-800 font-bold">0211 29273330</span>
          </a>
        </div>
      </div>

      {/* RIGHT · warm window-light photo */}
      <div className="relative order-1 lg:order-2 aspect-[4/5] h-auto sm:aspect-auto sm:h-[54vh] lg:h-auto overflow-hidden">
        <video
          src="/videos/hero-loop.mp4"
          poster="/images/brand/hero-gallery.jpg"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          aria-label="Pflegekraft liest das Dickies-Campus Journal am Fenster"
          className="absolute inset-0 w-full h-full object-cover object-[50%_5%] sm:object-[50%_30%]"
          data-testid="hero-video-loop"
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none hidden lg:block"
          style={{ background: "linear-gradient(to right, #F6E8DC 0%, rgba(246,232,220,0) 14%)" }}
        />
        <div
          aria-hidden="true"
          className="absolute inset-x-0 bottom-0 h-44 pointer-events-none"
          style={{ background: "linear-gradient(to top, rgba(43,31,26,0.55), transparent)" }}
        />
        <div className="absolute bottom-6 lg:bottom-10 left-6 lg:left-12 right-6">
          <div className="photo-caption font-poster uppercase tracking-[0.35em] text-cream-50/85 text-[10px] lg:text-[11px]">
            Akademie für Pflege · MMXXVI
          </div>
          <div className="photo-caption mt-2 font-magazine italic text-cream-50 text-[15.5px] lg:text-[17px]">
            Über 1.400 Pflegekräfte lernen bereits hier.
          </div>
        </div>
      </div>
    </section>
  );
}
